import { Link } from "react-router-dom";
import { Badge, Button, Card } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";

interface AudienceCard {
  who: string;
  start: string;
  startTo: string;
  then: Array<[string, string]>;
}

const AUDIENCES: AudienceCard[] = [
  {
    who: "CTO evaluating",
    start: "Security & provably-fair",
    startTo: "/security",
    then: [
      ["API surface", "/api-reference"],
      ["Pricing", "/games/vault-breaker"],
      ["Talk to us", "/contact"],
    ],
  },
  {
    who: "Backend integrator",
    start: "API reference",
    startTo: "/api-reference",
    then: [
      ["SDK & types", "/sdk"],
      ["Webhooks (v1 spec)", "/webhooks"],
      ["Try the demo", "/play"],
    ],
  },
  {
    who: "Frontend integrator",
    start: "Iframe & embed",
    startTo: "/api-reference#iframe",
    then: [
      ["Launch token spec", "/api-reference#launch-token"],
      ["Demo round", "/play"],
      ["Iframe embed reference", "/api-reference#iframe"],
    ],
  },
];

interface DocCard {
  title: string;
  blurb: string;
  to: string;
  cta: string;
}

const TILES: DocCard[] = [
  {
    title: "API reference",
    blurb: "Six REST endpoints, one WebSocket, the verify route. Schemas, error codes, curl.",
    to: "/api-reference",
    cta: "Read the contract →",
  },
  {
    title: "SDK & types",
    blurb: "TypeScript types via @vb/rgs-contract today. Hand-rolled adapter pattern. NPM SDK roadmap.",
    to: "/sdk",
    cta: "See the types →",
  },
  {
    title: "Webhooks",
    blurb: "Event contract for asynchronous round / session callbacks. v1 spec — delivery rolls out next sprint.",
    to: "/webhooks",
    cta: "Read the spec →",
  },
  {
    title: "Security",
    blurb: "Provably-fair flow, transport, auth, rate limits, disclosure. What we ship today, what we don't.",
    to: "/security",
    cta: "See security posture →",
  },
];

export function Docs() {
  return (
    <>
      <Seo
        title="Docs · Vault Breaker Studios"
        description="Integrator documentation for Vault Breaker — REST contract, SDK, webhooks, security, and integration patterns for casino operators."
        path="/docs"
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          DEVELOPERS
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(44px, 6vw, 72px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          Build with Vault Breaker
        </h1>
        <p style={{ fontSize: 17, color: "var(--fg-2)", marginTop: 14, maxWidth: 720, lineHeight: 1.55 }}>
          A casino studio's job is to ship a memorable round to the player; an operator's job is
          to mint sessions, settle wallets, and present the result. We do the first, you do the
          second, and a small REST contract sits between us. Most pilots integrate in a week.
        </p>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Quick start"
          title="From signed agreement to live round"
          lead="Three steps once an operator key is issued."
        />
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          }}
        >
          <Step n={1} title="Mint a session">
            POST <code>/rgs/session/launch</code> with your operator id, player id, currency. You
            get back a session id, a JWT, and a server-seed commitment hash.
          </Step>
          <Step n={2} title="Open + resolve the round">
            POST <code>/rgs/round/open</code> to debit the wallet. Stream the WebSocket. POST{" "}
            <code>/rgs/round/resolve</code> with the player's chosen multiplier.
          </Step>
          <Step n={3} title="Reveal & verify">
            After resolution, GET <code>/rgs/round/:id/seeds</code> to expose the server seed.
            Anyone — including the player — can recompute the bust point and confirm the hash.
          </Step>
        </div>
      </Section>

      <Section>
        <SectionHeading
          eyebrow="Pick your path"
          title="Three audiences, three starting pages"
          lead="Same docs, sequenced for the work in front of you."
        />
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          }}
        >
          {AUDIENCES.map((a) => (
            <Card key={a.who} style={{ display: "grid", gap: 14 }}>
              <span className="label" style={{ color: "var(--fg-3)" }}>
                {a.who}
              </span>
              <Link to={a.startTo} style={{ textDecoration: "none" }}>
                <Button variant="gold" fullWidth style={{ padding: "10px 16px", fontSize: 13 }}>
                  Start: {a.start}
                </Button>
              </Link>
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
                {a.then.map(([label, to]) => (
                  <li key={to}>
                    <Link to={to} style={{ fontSize: 13, color: "var(--fg-2)", textDecoration: "none" }}>
                      → {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Reference" title="All the docs, one tile each" />
        <div
          style={{
            display: "grid",
            gap: 16,
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          }}
        >
          {TILES.map((t) => (
            <Card key={t.to} style={{ display: "grid", gap: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18 }}>{t.title}</h3>
              <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>
                {t.blurb}
              </p>
              <Link to={t.to} style={{ textDecoration: "none" }}>
                <Button fullWidth>{t.cta}</Button>
              </Link>
            </Card>
          ))}
        </div>
      </Section>

      <Section>
        <SectionHeading eyebrow="Support" title="Stuck? Talk to a human." />
        <Card style={{ display: "grid", gap: 10 }}>
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)" }}>
            Pre-sales technical questions:{" "}
            <a href="mailto:hello@vaultbreaker.dev" style={{ color: "var(--cyan)" }}>
              hello@vaultbreaker.dev
            </a>
            <br />
            Security disclosures:{" "}
            <a href="mailto:hello@vaultbreaker.dev" style={{ color: "var(--cyan)" }}>
              hello@vaultbreaker.dev
            </a>
            <br />
            Live integration support (signed operators only): a private shared channel of your choice (email, Discord, or Telegram).
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <Link to="/contact" style={{ textDecoration: "none" }}>
              <Button variant="gold">CONTACT SALES</Button>
            </Link>
            <Link to="/play" style={{ textDecoration: "none" }}>
              <Button>Play the demo →</Button>
            </Link>
          </div>
        </Card>
      </Section>
    </>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <Card style={{ display: "grid", gap: 8 }}>
      <span
        style={{
          fontFamily: "var(--font-display)",
          fontSize: 36,
          lineHeight: 1,
          color: "var(--gold-bright)",
          letterSpacing: -1,
        }}
      >
        {String(n).padStart(2, "0")}
      </span>
      <h3 style={{ margin: 0, fontSize: 17 }}>{title}</h3>
      <p style={{ margin: 0, fontSize: 13, color: "var(--fg-2)", lineHeight: 1.55 }}>{children}</p>
    </Card>
  );
}
